import { useState, useMemo } from "react";
import { Search, AlertTriangle, ArrowRight, MapPin, BadgeCheck, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { Shelter } from "@/types/shelter";
import { isUrgentNeedsActive } from "@/types/shelter";

interface ShelterSelectionStepProps {
  shelters: Shelter[];
  selectedShelters: Shelter[];
  onSelectionChange: (shelters: Shelter[]) => void;
  onNext: () => void;
  isLoading?: boolean;
  initialShelterId?: string;
}

const MAX_SELECTION = 3;

export const ShelterSelectionStep = ({
  shelters,
  selectedShelters,
  onSelectionChange,
  onNext,
  isLoading,
  initialShelterId,
}: ShelterSelectionStepProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [showOnlyUrgent, setShowOnlyUrgent] = useState(false);
  const [selectedCity, setSelectedCity] = useState("");

  // Get unique cities - filtered by urgent if that filter is active
  const uniqueCities = useMemo(() => {
    const sheltersToConsider = showOnlyUrgent 
      ? shelters.filter(isUrgentNeedsActive)
      : shelters;
    return [...new Set(sheltersToConsider.map((s) => s.city))].sort((a, b) => a.localeCompare(b, "pl"));
  }, [shelters, showOnlyUrgent]);

  // Get the city of the initial shelter for prioritization
  const initialShelterCity = useMemo(() => {
    if (!initialShelterId) return null;
    const initial = shelters.find((s) => s.id === initialShelterId);
    return initial?.city || null;
  }, [shelters, initialShelterId]);

  // Count urgent shelters
  const urgentCount = useMemo(
    () => shelters.filter(isUrgentNeedsActive).length,
    [shelters]
  );

  // Sort and filter shelters
  const sortedAndFilteredShelters = useMemo(() => {
    let result = [...shelters];

    // Apply urgent filter
    if (showOnlyUrgent) {
      result = result.filter(isUrgentNeedsActive);
    }

    // Apply city filter
    if (selectedCity) {
      result = result.filter((s) => s.city === selectedCity);
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (s) =>
          s.name.toLowerCase().includes(query) ||
          s.city.toLowerCase().includes(query)
      );
    }

    // Sort: selected first, then same city, then rest alphabetically
    result.sort((a, b) => {
      const aSelected = selectedShelters.some((s) => s.id === a.id);
      const bSelected = selectedShelters.some((s) => s.id === b.id);

      // Selected shelters first
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;

      // Then same city as initial shelter
      if (initialShelterCity) {
        const aIsInitialCity = a.city === initialShelterCity;
        const bIsInitialCity = b.city === initialShelterCity;
        if (aIsInitialCity && !bIsInitialCity) return -1;
        if (!aIsInitialCity && bIsInitialCity) return 1;
      }

      // Then alphabetically
      return a.name.localeCompare(b.name, "pl");
    });

    return result;
  }, [shelters, searchQuery, selectedShelters, initialShelterCity, showOnlyUrgent, selectedCity]);

  const handleToggleShelter = (e: React.MouseEvent, shelter: Shelter) => {
    e.stopPropagation();
    e.preventDefault();
    const isSelected = selectedShelters.some((s) => s.id === shelter.id);
    if (isSelected) {
      onSelectionChange(selectedShelters.filter((s) => s.id !== shelter.id));
    } else if (selectedShelters.length < MAX_SELECTION) {
      onSelectionChange([...selectedShelters, shelter]);
    }
  };

  const isShelterSelected = (shelter: Shelter) =>
    selectedShelters.some((s) => s.id === shelter.id);

  const canSelect = selectedShelters.length < MAX_SELECTION;

  return (
    <div className="flex flex-col flex-1 min-h-0 gap-3">
      {/* Search row */}
      <div className="flex items-center gap-2 shrink-0">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Szukaj..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 h-9 text-sm"
          />
        </div>
      </div>

      {/* Filters row */}
      <div className="flex items-center gap-2 flex-wrap shrink-0">
        <Button
          variant={showOnlyUrgent ? "default" : "outline"}
          size="sm"
          onClick={() => {
            const newValue = !showOnlyUrgent;
            setShowOnlyUrgent(newValue);
            // Reset city if it doesn't have urgent shelters
            if (newValue && selectedCity) {
              const hasUrgentInCity = shelters.some(
                (s) => s.city === selectedCity && isUrgentNeedsActive(s)
              );
              if (!hasUrgentInCity) {
                setSelectedCity("");
              }
            }
          }}
          disabled={urgentCount === 0}
          className={`text-xs h-8 px-2.5 ${
            showOnlyUrgent
              ? "bg-orange-500 hover:bg-orange-600 text-white border-orange-500"
              : "text-orange-600 border-orange-300 hover:bg-orange-50"
          }`}
        >
          <AlertTriangle className="w-3.5 h-3.5 mr-1" />
          Pilne ({urgentCount})
        </Button>

        <Select 
          value={selectedCity} 
          onValueChange={(val) => setSelectedCity(val === "all" ? "" : val)}
        >
          <SelectTrigger className="h-8 text-xs w-auto min-w-[90px] max-w-[130px] shrink-0">
            <MapPin className="w-3.5 h-3.5 mr-1 shrink-0 text-muted-foreground" />
            <SelectValue placeholder="Miasto" />
          </SelectTrigger>
          <SelectContent className="z-[1200]">
            <SelectItem value="all">Wszystkie</SelectItem>
            {uniqueCities.map((city) => (
              <SelectItem key={city} value={city}>
                {city}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <span className="text-xs text-muted-foreground whitespace-nowrap ml-auto">
          <strong>{selectedShelters.length}</strong>/{MAX_SELECTION}
        </span>
      </div>

      {/* Shelter list - flex-1 to fill remaining space */}
      <ScrollArea className="flex-1 min-h-0 border rounded-lg">
        {isLoading ? (
          <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
            Ładowanie...
          </div>
        ) : sortedAndFilteredShelters.length === 0 ? (
          <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
            Nie znaleziono schronisk
          </div>
        ) : (
          <div className="p-1.5 space-y-0.5">
            {sortedAndFilteredShelters.map((shelter) => {
              const isSelected = isShelterSelected(shelter);
              const isUrgent = isUrgentNeedsActive(shelter);
              const isDisabled = !isSelected && !canSelect;

              return (
                <button
                  key={shelter.id}
                  onClick={(e) => !isDisabled && handleToggleShelter(e, shelter)}
                  disabled={isDisabled}
                  className={`flex items-center gap-2.5 p-2.5 rounded-lg w-full text-left transition-all duration-200 ${
                    isSelected
                      ? "bg-primary/10 ring-1 ring-primary/40"
                      : isDisabled
                      ? "opacity-50 cursor-not-allowed bg-muted/30"
                      : "hover:bg-muted/60"
                  }`}
                >
                  {/* Custom checkbox */}
                  <div
                    className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 transition-all duration-200 ${
                      isSelected
                        ? "bg-primary text-primary-foreground"
                        : "border-2 border-muted-foreground/30 bg-background"
                    }`}
                  >
                    {isSelected && <Check className="w-3 h-3 stroke-[3]" />}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-1.5 flex-wrap">
                      <span className="font-medium text-sm leading-tight">
                        {shelter.name}
                      </span>
                      {shelter.organization_verified && (
                        <BadgeCheck className="w-4 h-4 text-primary shrink-0" />
                      )}
                      {isUrgent && (
                        <Badge
                          variant="destructive"
                          className="text-[10px] px-1.5 py-0 h-4 bg-orange-500 shrink-0"
                        >
                          PILNE
                        </Badge>
                      )}
                    </div>

                    <span className="text-xs text-muted-foreground">
                      {shelter.city}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </ScrollArea>

      {/* Next button */}
      <Button
        onClick={onNext}
        disabled={selectedShelters.length === 0}
        className="w-full h-10 shrink-0"
      >
        Dalej
        <ArrowRight className="w-4 h-4 ml-2" />
      </Button>
    </div>
  );
};